import { Button, LoadingSpinner, SegmentedControl, Tooltip, Typography } from '@neo4j-ndl/react';
import { PlusIconOutline, Squares2X2IconOutline, TableCellsIconOutline } from '@neo4j-ndl/react/icons';
import { type FleetManagementTypes, useSetting } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { SearchField } from '@nx/ui';
import { useCallback, useEffect, useState } from 'react';

import { ExploreIconOutline } from '../../../upx/src/components/icons';
import FleetManagementBlank from '../assets/fleet-management.svg';
import { ApiErrorBanner } from '../shared/components';
import { DeploymentInspectDrawer } from './deployments/deployment-inspect-drawer';
import { DeploymentList } from './deployments/deployments-list';
import { DeploymentTable } from './deployments/deployments-table';
import { DeploymentGraph } from './graph/deployments-graph';
import { useDeploymentFilters } from './hooks/use-deployment-filters';
import { useDeployments } from './hooks/use-deployments';
import { usePermissions } from './hooks/use-permissions';
import { ConnectDeploymentWizardModal } from './modals/create-deplyment-modals/connect-deployment-wizard-modal';
import { DeploymentFilter } from './shared/components/deployment-filter';
import { FilterIconButton } from './shared/components/filter-icon-button';

const DeploymentsView = () => {
  const [deploymentDisplayType, setDeploymentDisplayType] = useSetting('ops', 'deploymentDisplay');
  const [isConnectDeploymentModalOpen, setConnectDeploymentModalOpen] = useState<boolean>(false);

  const { deployments, isDeploymentsLoading, error } = useDeployments({ shouldPoll: true });

  const { filters, availableFilters, onFilterChange, clearAllFilters, hasActiveFilters, filteredDeployments } =
    useDeploymentFilters(deployments);

  const [deploymentDetails, setDeploymentDetails] = useState<FleetManagementTypes.DeploymentDetails | null>(null);

  const handleDisplayDbmsDetails = useCallback((deployment: FleetManagementTypes.DeploymentDetails) => {
    setDeploymentDetails(deployment);
  }, []);

  const handleCloseDbmsDetails = useCallback(() => {
    setDeploymentDetails(null);
  }, []);

  useEffect(() => {
    if (deploymentDetails && deployments.length > 0) {
      const updatedDeployment = deployments.find((d) => d.deploymentId === deploymentDetails.deploymentId);
      if (updatedDeployment && updatedDeployment !== deploymentDetails) {
        setDeploymentDetails(updatedDeployment);
      }
    }
  }, [deployments, deploymentDetails]);

  const isTokenCreationAllowed = usePermissions().isAllowed('tokens', 'write');

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (error) {
    return <ApiErrorBanner className="m-4" error={error} />;
  }

  return (
    <>
      <ConnectDeploymentWizardModal
        isOpen={isConnectDeploymentModalOpen}
        closeModal={() => setConnectDeploymentModalOpen(false)}
      />
      {isDeploymentsLoading && <LoadingSpinner size="large" className="absolute left-1/2 top-1/2" />}
      {deployments.length === 0 && !isDeploymentsLoading && (
        <div className="my-16 flex h-full flex-col items-center justify-center gap-16 md:flex-row">
          <img src={FleetManagementBlank} draggable="false" />
          <div className="flex max-w-[420px] flex-col gap-6">
            <h4>Connect an external deployment</h4>
            <Typography variant="body-large">
              Monitor your external deployments and streamline your workflow by accessing saved connections instantly.
            </Typography>
            <Tooltip type="simple" isDisabled={isTokenCreationAllowed}>
              <Tooltip.Trigger hasButtonWrapper>
                <Button onClick={() => setConnectDeploymentModalOpen(true)} isDisabled={!isTokenCreationAllowed}>
                  <PlusIconOutline className="size-4" />
                  Add deployment
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>Project admin role is required to manage deployments</Tooltip.Content>
            </Tooltip>
          </div>
        </div>
      )}
      {deployments.length > 0 && (
        <>
          {isNotNullish(deploymentDetails) && (
            <DeploymentInspectDrawer deployment={deploymentDetails} onClose={handleCloseDbmsDetails} />
          )}
          <div className="relative mt-2 flex h-full flex-col">
            <div className="mb-2 flex h-14 w-full items-center justify-between gap-4 py-[10px]">
              <div className="flex basis-[400px] gap-2">
                <SearchField
                  className="min-w-36 grow"
                  value={filters.search}
                  onChange={(e) => onFilterChange({ search: e.target.value })}
                  htmlAttributes={{ 'aria-label': 'Search for DBMSs' }}
                />
                <FilterIconButton handleClick={handleClick} hasActiveFilters={hasActiveFilters} />
                <DeploymentFilter
                  open={open}
                  anchorEl={anchorEl}
                  handleClose={handleClose}
                  filters={filters}
                  onFilterChange={onFilterChange}
                  clearAllFilters={clearAllFilters}
                  availableFilters={availableFilters}
                  filteredDeployments={filteredDeployments}
                />
              </div>
              <div className="flex shrink-0 gap-4">
                <SegmentedControl
                  onChange={(changedId) => setDeploymentDisplayType(changedId)}
                  hasOnlyIcons
                  size="small"
                  selected={deploymentDisplayType}
                >
                  <SegmentedControl.Item
                    value="table"
                    htmlAttributes={{
                      'aria-label': 'View instances in Table view',
                    }}
                  >
                    <TableCellsIconOutline className="size-4" />
                  </SegmentedControl.Item>
                  <SegmentedControl.Item
                    value="graph"
                    htmlAttributes={{
                      'aria-label': 'View instances in Hierarchy view',
                    }}
                  >
                    <ExploreIconOutline className="size-4" />
                  </SegmentedControl.Item>
                  <SegmentedControl.Item
                    value="list"
                    htmlAttributes={{
                      'aria-label': 'View instances in List view',
                    }}
                  >
                    <Squares2X2IconOutline className="size-4" />
                  </SegmentedControl.Item>
                </SegmentedControl>
                <Tooltip type={'simple'} isDisabled={isTokenCreationAllowed}>
                  <Tooltip.Trigger hasButtonWrapper>
                    <Button
                      fill="filled"
                      color="primary"
                      isDisabled={!isTokenCreationAllowed}
                      onClick={() => setConnectDeploymentModalOpen(true)}
                    >
                      <PlusIconOutline className="size-4" />
                      Add deployment
                    </Button>
                  </Tooltip.Trigger>
                  <Tooltip.Content>Project admin role is required to manage deployments</Tooltip.Content>
                </Tooltip>
              </div>
            </div>
            {deployments.length > 0 && filteredDeployments.length === 0 && hasActiveFilters && (
              <div className="my-16 flex h-full flex-col items-center justify-center gap-16 md:flex-row">
                <img src={FleetManagementBlank} draggable="false" />
                <div className="flex max-w-[420px] flex-col gap-6">
                  <h4>No deployments match your filters</h4>
                  <Typography variant="body-large">
                    Try adjusting your filter criteria to see more deployments.
                  </Typography>
                  <Button onClick={() => clearAllFilters()}>Reset filters</Button>
                </div>
              </div>
            )}
            {deploymentDisplayType === 'table' && filteredDeployments.length > 0 && (
              <DeploymentTable
                deployments={filteredDeployments}
                searchFilter={filters.search}
                setSearchFilter={(search) => onFilterChange({ search })}
                handleDisplayDbmsDetails={handleDisplayDbmsDetails}
              />
            )}
            {deploymentDisplayType === 'graph' && filteredDeployments.length > 0 && (
              <DeploymentGraph
                deployments={filteredDeployments}
                searchFilter={filters.search}
                containerClassName={'h-[calc(100vh-300px)]'}
                isInDetailsView={false}
                handleDisplayDbmsDetails={handleDisplayDbmsDetails}
              />
            )}
            {deploymentDisplayType === 'list' && filteredDeployments.length > 0 && (
              <DeploymentList
                deployments={filteredDeployments}
                searchFilter={filters.search}
                handleDisplayDbmsDetails={handleDisplayDbmsDetails}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default DeploymentsView;
